/**
 * Origin/source validation for `window.message` handlers the host SPA owns
 * (pen-test finding F-13).
 *
 * The embed adapters each own their iframe ref, so they can check
 *
 *     e.origin !== allowedOrigin       -> drop
 *     e.source !== iframe.contentWindow -> drop
 *
 * The host layout has no iframe ref — it listens for `nucleus:navigate` posted
 * by whichever embed is currently mounted — so it makes the same two checks
 * against the document's own iframes: the message must come from a frame this
 * page rendered, and its origin must match that frame's declared `src` origin.
 *
 * That rejects `window.opener`, an unrelated top-level window, a frame nested
 * inside an embed (whose `contentWindow` is not one of ours), and any frame that
 * has been swapped to another origin since it was rendered.
 */

const originOf = (url: string): string | null => {
  try {
    return new URL(url, window.location.href).origin;
  } catch {
    return null;
  }
};

/**
 * `true` when `event` came from an iframe in this document whose `src` origin
 * is `event.origin`. Same-origin messages from the page itself are also
 * accepted, since the app posts to itself in a few places.
 */
export const isTrustedEmbedMessage = (event: MessageEvent): boolean => {
  if (!event.source) {
    return false;
  }

  if (event.source === window && event.origin === window.location.origin) {
    return true;
  }

  const frames = Array.from(document.querySelectorAll("iframe"));
  return frames.some((frame) => {
    if (frame.contentWindow !== event.source) {
      return false;
    }
    const frameOrigin = frame.getAttribute("src")
      ? originOf(frame.getAttribute("src") as string)
      : null;
    return frameOrigin !== null && frameOrigin === event.origin;
  });
};
